import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Check, Loader2, Mic, Play, Square } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { stopReading } from "@/lib/readingAudio";

const MAX_MS = 30000;
const pickType = () => ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg"].find((m) => window.MediaRecorder?.isTypeSupported?.(m)) || "";

// The child records themself reading the page; the recording is saved privately for the parent.
export default function StoryRecorder({ childId, storyId, page, fr }) {
  const [state, setState] = useState("idle");
  const [url, setUrl] = useState(null);
  const rec = useRef(null);
  const timer = useRef(null);
  const discard = useRef(false);
  const cancel = () => { clearTimeout(timer.current); if (rec.current?.state === "recording") { discard.current = true; rec.current.stop(); } };
  useEffect(() => cancel, []);
  useEffect(() => { cancel(); setState("idle"); setUrl(null); }, [page]);

  const start = async () => {
    stopReading();
    if (!navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) {
      toast.error(fr ? "Ce navigateur ne peut pas enregistrer." : "This browser cannot record.");
      return;
    }
    let stream;
    try { stream = await navigator.mediaDevices.getUserMedia({ audio: true }); } catch {
      toast.error(fr ? "Autorise le micro pour t'enregistrer." : "Allow the microphone to record yourself.");
      return;
    }
    const type = pickType();
    const r = new MediaRecorder(stream, type ? { mimeType: type } : undefined);
    const chunks = [];
    r.ondataavailable = (e) => e.data.size && chunks.push(e.data);
    r.onstop = async () => {
      stream.getTracks().forEach((tr) => tr.stop());
      if (discard.current) { discard.current = false; return; }
      const blob = new Blob(chunks, { type: (r.mimeType || type || "audio/webm").split(";")[0] });
      setUrl(URL.createObjectURL(blob));
      setState("saving");
      const fd = new FormData();
      fd.append("story_id", storyId);
      fd.append("page", String(page));
      fd.append("file", blob, `page-${page}.${blob.type.includes("mp4") ? "m4a" : blob.type.split("/")[1]}`);
      try {
        await api.post(`/reading/${childId}/recordings`, fd);
        setState("saved");
        toast.success(fr ? "Bravo ! Tes parents pourront t'écouter." : "Well done! Your parents can listen to you.");
      } catch {
        setState("error");
        toast.error(fr ? "L'enregistrement n'a pas été sauvegardé." : "The recording was not saved.");
      }
    };
    rec.current = r;
    r.start();
    setState("recording");
    timer.current = setTimeout(() => r.state === "recording" && r.stop(), MAX_MS);
  };
  const stop = () => { clearTimeout(timer.current); if (rec.current?.state === "recording") rec.current.stop(); };
  const play = () => { stopReading(); new Audio(url).play().catch(() => {}); };

  return (
    <div className="flex flex-wrap items-center justify-center gap-3" data-testid="story-recorder">
      {state === "recording" ? (
        <motion.button onClick={stop} animate={{ scale: [1, 1.08, 1] }} transition={{ repeat: Infinity, duration: 1 }} data-testid="story-record-stop"
          className="rounded-full border-[3px] border-slate-900 px-5 h-12 font-bold bg-red-500 text-white inline-flex items-center gap-2 shadow-[4px_4px_0_0_#0F172A]">
          <Square size={18} /> {fr ? "Stop" : "Stop"}
        </motion.button>
      ) : (
        <motion.button whileTap={{ scale: 0.95 }} onClick={start} disabled={state === "saving"} data-testid="story-record-start"
          className="rounded-full border-[3px] border-slate-900 px-5 h-12 font-bold bg-pink-400 text-white inline-flex items-center gap-2 shadow-[4px_4px_0_0_#0F172A] disabled:opacity-60">
          <Mic size={18} /> {state === "idle" ? (fr ? "Je m'enregistre" : "Record me") : (fr ? "Recommencer" : "Record again")}
        </motion.button>
      )}
      {url && state !== "recording" && (
        <motion.button whileTap={{ scale: 0.95 }} onClick={play} data-testid="story-record-play"
          className="rounded-full border-[3px] border-slate-900 px-5 h-12 font-bold bg-white inline-flex items-center gap-2 shadow-[4px_4px_0_0_#0F172A]">
          <Play size={18} /> {fr ? "Je m'écoute" : "Listen to me"}
        </motion.button>
      )}
      {state === "saving" && <Loader2 className="animate-spin" data-testid="story-record-saving" />}
      {state === "saved" && <span className="inline-flex items-center gap-1 font-bold text-green-700" data-testid="story-record-saved"><Check size={18} /> {fr ? "Envoyé à tes parents" : "Sent to your parents"}</span>}
    </div>
  );
}
